import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { allVideos, categoryList, siteInfo } from "@/lib/videoData";
import { usePageMeta } from "@/hooks/usePageMeta";

const values = [
  {
    icon: "ri-compass-3-line",
    title: "内容优先",
    text: "先想清楚用户想看什么，再决定页面长什么样。所有模块都围绕“更快找到内容”设计。",
  },
  {
    icon: "ri-shield-check-line",
    title: "信息可信",
    text: "主演、导演、年份、集数与更新状态逐项标注，避免标题党与模糊描述。",
  },
  {
    icon: "ri-heart-3-line",
    title: "克制设计",
    text: "深蓝与暖黄的配色、清晰的层级与留白，让浏览本身成为一件轻松的事。",
  },
];

const contacts = [
  { icon: "ri-mail-line", label: "内容与商务合作", value: siteInfo.email },
  { icon: "ri-phone-line", label: "客服热线（9:00-21:00）", value: siteInfo.phone },
  { icon: "ri-map-pin-line", label: "内容中心地址", value: siteInfo.address },
];

export default function AboutPage() {
  const [copied, setCopied] = useState(false);

  usePageMeta(
    "关于我们 - 糖豆视频 精选影视综艺内容导览平台",
    "了解糖豆视频的定位、内容范围与团队理念。糖豆视频专注影视综艺内容整理与导览，提供电影、电视剧、综艺的完整资料与更新信息，并开放联系与反馈渠道。",
    "关于糖豆视频,糖豆视频介绍,影视内容平台,联系我们,影视综艺导览",
  );

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(siteInfo.email);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-100">
      <Navbar />

      <main className="w-full flex-1">
        <section className="relative w-full overflow-hidden bg-primary-950 py-12 md:py-16">
          <div className="absolute -left-20 top-0 h-64 w-64 rounded-full bg-accent-500/10 blur-3xl" />
          <div className="absolute -right-16 bottom-0 h-72 w-72 rounded-full bg-primary-700/40 blur-3xl" />
          <div className="relative mx-auto w-full max-w-[1360px] px-4 md:px-8">
            <nav className="flex items-center gap-1.5 text-xs text-background-400" aria-label="面包屑导航">
              <Link to="/" className="transition-colors hover:text-accent-300">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-sm" />
              <span className="font-medium text-background-200">关于我们</span>
            </nav>

            <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1.3fr_1fr] lg:items-end">
              <div>
                <span className="text-[11px] font-bold tracking-[0.26em] text-accent-400">ABOUT US</span>
                <h1 className="mt-3 font-heading text-3xl font-extrabold leading-tight tracking-tight text-background-50 md:text-[46px]">
                  关于糖豆视频
                </h1>
                <p className="mt-4 max-w-2xl text-sm leading-7 text-background-300/90 md:text-[15px]">
                  糖豆视频是一个专注影视综艺内容的展示与导览平台。我们相信，好的内容值得被认真地组织起来：分类要清楚，信息要完整，浏览要顺畅。自 {siteInfo.since} 年上线以来，我们持续整理电影、电视剧与综艺资料，帮助用户用更少的时间找到更想看的作品。
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: `${allVideos.length}`, label: "精选内容" },
                  { value: "3", label: "内容分类" },
                  { value: "7×24", label: "更新追踪" },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-lg border border-primary-800/80 bg-primary-900/60 px-3 py-4 text-center"
                  >
                    <p className="font-heading text-xl font-extrabold text-background-50">{stat.value}</p>
                    <p className="mt-1 text-[10px] text-background-400">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1360px] px-4 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.15fr] lg:gap-12">
            <div>
              <span className="text-[11px] font-bold tracking-[0.24em] text-accent-700">OUR MISSION</span>
              <h2 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[30px]">
                让影视内容的浏览，
                <br className="hidden sm:block" />
                回到简单而清晰的样子
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-7 text-foreground-700">
                <p>
                  我们不做复杂的推荐算法堆叠，而是把每部作品的资料整理干净：它是什么类型，谁主演，哪一年上映，更新到第几集。用户打开页面，就能判断这部内容是不是自己想看的。
                </p>
                <p>
                  站内所有影片信息、封面与简介均为演示数据，用于展示内容组织与交互方式。我们希望通过这套结构，说明一个内容平台应该如何尊重用户的注意力。
                </p>
              </div>

              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-quill-pen-line text-base text-primary-600" />
                  内容范围
                </h3>
                <ul className="mt-3 space-y-2.5">
                  {categoryList.map((category) => {
                    const count = allVideos.filter((item) => item.category === category.slug).length;
                    return (
                      <li key={category.slug}>
                        <Link
                          to={`/category/${category.slug}`}
                          className="group flex items-center justify-between gap-3 rounded-md px-2 py-2 transition-colors hover:bg-background-100"
                        >
                          <span className="flex min-w-0 items-center gap-2 text-xs text-foreground-800">
                            <i className="ri-checkbox-circle-fill text-sm text-accent-500" />
                            <span className="font-medium">{category.name}</span>
                            <span className="line-clamp-1 text-foreground-500">{category.short}</span>
                          </span>
                          <span className="shrink-0 text-[11px] font-semibold text-primary-700">{count} 部</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className="space-y-3">
              {values.map((value) => (
                <div
                  key={value.title}
                  className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-200"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                    <i className={`${value.icon} text-xl`} />
                  </span>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950">{value.title}</h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{value.text}</p>
                  </div>
                </div>
              ))}

              <div className="rounded-lg bg-secondary-100 p-5">
                <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-secondary-900">
                  <i className="ri-team-line text-base text-secondary-600" />
                  我们的团队
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-secondary-900/80">
                  一支由内容编辑、产品与前端工程师组成的小团队，分布在上海与成都。团队规模不大，但每一条影片资料都会经过人工核对后再上线。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-background-200/80 bg-background-50 py-10 md:py-14">
          <div className="mx-auto w-full max-w-[1360px] px-4 md:px-8">
            <div className="mb-6">
              <span className="text-[11px] font-bold tracking-[0.24em] text-accent-700">CONTACT</span>
              <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-[30px]">
                联系我们
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                无论是内容纠错、影片推荐还是商务合作，都欢迎直接联系我们。工作日会在两个工作日内回复。
              </p>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.05fr_1fr]">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <h3 className="font-heading text-base font-bold text-foreground-950">联系方式</h3>
                <ul className="mt-4 space-y-4">
                  {contacts.map((contact) => (
                    <li key={contact.label} className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                        <i className={`${contact.icon} text-base`} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[11px] text-foreground-500">{contact.label}</p>
                        <p className="mt-0.5 break-all text-sm font-medium text-foreground-900">{contact.value}</p>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-5 flex flex-wrap gap-2">
                  <a
                    href={`mailto:${siteInfo.email}`}
                    className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-800 px-4 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-900"
                  >
                    <i className="ri-mail-send-line text-sm" />
                    发送邮件
                  </a>
                  <button
                    type="button"
                    onClick={copyEmail}
                    className="flex h-10 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className={copied ? "ri-check-line text-sm text-primary-700" : "ri-file-copy-line text-sm"} />
                    {copied ? "邮箱已复制" : "复制邮箱地址"}
                  </button>
                </div>

                <div className="mt-5 border-t border-background-200 pt-4">
                  <p className="text-[11px] font-medium text-foreground-700">关注我们</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {[
                      { icon: "ri-weibo-fill", label: "微博" },
                      { icon: "ri-wechat-fill", label: "微信公众号" },
                      { icon: "ri-bilibili-fill", label: "哔哩哔哩" },
                      { icon: "ri-youtube-fill", label: "YouTube" },
                    ].map((item) => (
                      <span
                        key={item.label}
                        title={item.label}
                        className="flex h-9 items-center gap-1.5 rounded-md bg-background-100 px-3 text-[11px] text-foreground-700"
                      >
                        <i className={`${item.icon} text-sm text-primary-700`} />
                        {item.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <iframe
                  title="糖豆视频内容中心位置"
                  src="https://manka07.cn/maps?q=%E4%B8%8A%E6%B5%B7%E5%B8%82%E5%BE%90%E6%B1%87%E5%8C%BA%E9%BE%99%E8%85%BE%E5%A4%A7%E9%81%932555%E5%8F%B7&output=embed"
                  className="h-[260px] w-full border-0 md:h-[320px]"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <div className="p-5">
                  <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                    <i className="ri-building-2-line text-base text-primary-600" />
                    糖豆内容中心
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{siteInfo.address}</p>
                  <p className="mt-1.5 text-[11px] text-foreground-500">
                    来访请提前预约，工作日 10:00 - 18:00 有同事接待。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1360px] px-4 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-copyright-line text-lg text-primary-600" />
                版权说明
              </h2>
              <p className="mt-3 text-xs leading-6 text-foreground-600">
                本站为演示性质的影视内容导览站点，站内影片名称、简介、评分、热度与封面均为虚构或生成的示例内容，
                不对应任何真实影视作品的发行信息，也不提供任何影片的在线播放服务。
              </p>
              <p className="mt-2 text-xs leading-6 text-foreground-600">
                如本站内容无意中侵犯了您的合法权益，请通过上方邮箱与我们联系，我们会在核实后及时处理。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-error-warning-line text-lg text-accent-600" />
                免责声明
              </h2>
              <p className="mt-3 text-xs leading-6 text-foreground-600">
                站内所有播放按钮、进度条与集数切换均为前端交互演示，不加载、不存储、不分发任何真实视频文件。
                收藏功能仅将影片编号保存在您当前浏览器的本地存储中，不会上传到服务器。
              </p>
              <p className="mt-2 text-xs leading-6 text-foreground-600">
                用户因使用本站信息作出的任何决定，由用户自行承担相应责任。
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-col items-center justify-between gap-3 rounded-lg bg-primary-950 px-5 py-6 sm:flex-row">
            <div>
              <p className="font-heading text-base font-bold text-background-50">准备好挑一部今晚看的了吗？</p>
              <p className="mt-1 text-[11px] text-background-400">30 部精选演示内容，支持筛选、排序与收藏。</p>
            </div>
            <Link
              to="/category/movie"
              className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 text-xs font-bold text-foreground-950 transition-colors hover:bg-accent-400"
            >
              进入片库
              <i className="ri-arrow-right-line text-sm" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}